import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './FloatingGallery.css';

// Import Hedera utilities
import { fetchHederaCollection } from './utils/hederaUtils';

const FloatingGallery = () => {
    const canvasRef = useRef(null);
    const imagesRef = useRef([]);
    const animationRef = useRef(null);
    const imageObjectsRef = useRef([]);
    const [nfts, setNfts] = useState([]);
    
    // Hedera Collection ID - The Immuta Collection Vol 1
    const HEDERA_COLLECTION_ID = '0.0.6299009';

    // Fetch Hedera NFTs when component mounts
    useEffect(() => {
        const loadHederaNfts = async () => {
            try {
                const result = await fetchHederaCollection(HEDERA_COLLECTION_ID);
                setNfts(result.nfts);
                console.log('Loaded NFTs for floating gallery:', result.nfts.length);
            } catch (error) {
                console.error('Failed to load Hedera NFTs for floating gallery:', error);
            }
        };
        
        loadHederaNfts();
    }, []);

    // Initialize floating images
    useEffect(() => {
        if (nfts.length === 0) return; // Wait until NFTs are loaded
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        // Set canvas dimensions
        const updateCanvasSize = () => {
            canvas.width = window.innerWidth;
            canvas.height = 500; // Increased height for better visual impact
        };
        
        updateCanvasSize();
        window.addEventListener('resize', updateCanvasSize);
        
        // Load NFT images
        const loadNftImages = () => {
            // Clear previous image objects
            imageObjectsRef.current = [];
            
            // Create image objects for each NFT
            nfts.forEach((nft, index) => {
                if (nft.imageUrl) {
                    const img = new Image();
                    img.src = nft.imageUrl;
                    img.onload = () => {
                        console.log(`NFT image ${index} loaded successfully`);
                    };
                    img.onerror = () => {
                        console.error(`Failed to load NFT image ${index}`);
                    };
                    
                    imageObjectsRef.current.push(img);
                }
            });
        };
        
        loadNftImages();
        
        // Create floating image parameters
        const createImages = () => {
            const images = [];
            const imageCount = Math.min(15, nfts.length); // Number of floating images
            
            for (let i = 0; i < imageCount; i++) {
                // Create random sizes and positions - larger to fill more of the canvas
                const size = 150 + Math.random() * 150;
                images.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    size: size,
                    speedX: (Math.random() - 0.5) * 0.5,
                    speedY: (Math.random() - 0.5) * 0.5,
                    rotation: Math.random() * Math.PI * 2,
                    rotationSpeed: (Math.random() - 0.5) * 0.01,
                    opacity: 0.7 + Math.random() * 0.3,
                    nftIndex: i % nfts.length
                });
            }
            
            imagesRef.current = images;
        };
        
        createImages();
        
        // Animation function
        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            // Draw each image
            imagesRef.current.forEach(img => {
                // Update position
                img.x += img.speedX;
                img.y += img.speedY;
                img.rotation += img.rotationSpeed;
                
                // Boundary check
                if (img.x < -img.size) img.x = canvas.width + img.size;
                if (img.x > canvas.width + img.size) img.x = -img.size;
                if (img.y < -img.size) img.y = canvas.height + img.size;
                if (img.y > canvas.height + img.size) img.y = -img.size;
                
                ctx.save();
                ctx.translate(img.x, img.y);
                ctx.rotate(img.rotation);
                ctx.globalAlpha = img.opacity;
                
                // Draw the NFT image if available
                const nftImage = imageObjectsRef.current[img.nftIndex];
                if (nftImage && nftImage.complete) {
                    try {
                        // Calculate dimensions to preserve aspect ratio
                        let drawWidth = img.size;
                        let drawHeight = img.size;
                        let offsetX = -img.size/2;
                        let offsetY = -img.size/2;
                        
                        // Adjust dimensions to maintain aspect ratio
                        if (nftImage.width > nftImage.height) {
                            // Landscape image
                            drawHeight = (nftImage.height / nftImage.width) * drawWidth;
                            offsetY = -drawHeight/2;
                        } else if (nftImage.height > nftImage.width) {
                            // Portrait image
                            drawWidth = (nftImage.width / nftImage.height) * drawHeight;
                            offsetX = -drawWidth/2;
                        }
                        
                        // Draw the NFT image with preserved aspect ratio
                        ctx.drawImage(nftImage, offsetX, offsetY, drawWidth, drawHeight);
                        
                        // Add a subtle border that matches the image dimensions
                        ctx.strokeStyle = '#000';
                        ctx.lineWidth = 1;
                        ctx.strokeRect(offsetX, offsetY, drawWidth, drawHeight);
                    } catch (e) {
                        console.error('Error drawing image:', e);
                        // Fallback to placeholder if image drawing fails
                        drawPlaceholder(img);
                    }
                } else {
                    // Draw placeholder until image loads
                    drawPlaceholder(img);
                }
                
                ctx.restore();
            });
            
            // Helper function to draw placeholder
            function drawPlaceholder(imgObj) {
                // Draw a frame/placeholder
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 2;
                ctx.strokeRect(-imgObj.size/2, -imgObj.size/2, imgObj.size, imgObj.size);
                
                // Add a diagonal line to make it look like a placeholder
                ctx.beginPath();
                ctx.moveTo(-imgObj.size/2, -imgObj.size/2);
                ctx.lineTo(imgObj.size/2, imgObj.size/2);
                ctx.stroke();
                
                ctx.beginPath();
                ctx.moveTo(imgObj.size/2, -imgObj.size/2);
                ctx.lineTo(-imgObj.size/2, imgObj.size/2);
                ctx.stroke();
            }
            
            animationRef.current = requestAnimationFrame(animate);
        };
        
        animate();
        
        // Cleanup
        return () => {
            window.removeEventListener('resize', updateCanvasSize);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [nfts]); // Re-initialize when NFTs are loaded
    
    return (
        <div className="floating-gallery">
            <canvas ref={canvasRef} className="floating-gallery-canvas"></canvas>
            <div className="floating-gallery-overlay">
                <div className="floating-gallery-content">
                    <h2 className="floating-gallery-tagline">Where Digital Art Meets Blockchain Innovation</h2>
                    <Link 
                        to="/" 
                        onClick={() => window.dispatchEvent(new CustomEvent('navigateToGallery'))} 
                        className="floating-gallery-cta"
                    >
                        Explore the Art
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default FloatingGallery;
